import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Utensils, ShoppingBag, Bike, Lock, Store, CalendarClock, UserPlus, CreditCard } from 'lucide-react';

/**
 * iter893 — One-time order-type chooser shown at the start of every new
 * POS invoice. The cashier picks the order type ONCE and it is locked for
 * the rest of the invoice.
 *
 * Universal POS: the available tiles are driven by the branch's configured
 * industry `order_types` (POS_INDUSTRIES) passed in via `orderTypes`. A
 * restaurant sees Dine-in/Takeaway/Delivery; a retail/service branch sees
 * its own types (counter / appointment / walk_in / credit …). Falls back to
 * the restaurant trio when no config is supplied (backward compatible).
 */
const OPTION_MAP = {
  dine_in:     { key: 'dine_in',     label: 'Dine In',     caption: 'Guest eats here', Icon: Utensils,      tone: 'from-orange-500 to-amber-600', ring: 'hover:ring-orange-300' },
  takeaway:    { key: 'takeaway',    label: 'Takeaway',    caption: 'Pack to go',      Icon: ShoppingBag,   tone: 'from-emerald-500 to-teal-600', ring: 'hover:ring-emerald-300' },
  delivery:    { key: 'delivery',    label: 'Delivery',    caption: 'Rider pickup',    Icon: Bike,          tone: 'from-sky-500 to-indigo-600',   ring: 'hover:ring-sky-300' },
  counter:     { key: 'counter',     label: 'Counter',     caption: 'Walk-up sale',    Icon: Store,         tone: 'from-slate-500 to-slate-700',  ring: 'hover:ring-slate-300' },
  appointment: { key: 'appointment', label: 'Appointment', caption: 'Booked service',  Icon: CalendarClock, tone: 'from-violet-500 to-purple-600', ring: 'hover:ring-violet-300' },
  walk_in:     { key: 'walk_in',     label: 'Walk-In',     caption: 'No booking',      Icon: UserPlus,      tone: 'from-cyan-500 to-blue-600',    ring: 'hover:ring-cyan-300' },
  credit:      { key: 'credit',      label: 'Credit',      caption: 'Pay later',       Icon: CreditCard,    tone: 'from-rose-500 to-pink-600',    ring: 'hover:ring-rose-300' },
};
const DEFAULT_TYPES = ['dine_in', 'takeaway', 'delivery'];

export default function OrderTypeChooserDialog({ open, onChoose, orderTypes }) {
  const keys = (Array.isArray(orderTypes) && orderTypes.length ? orderTypes : DEFAULT_TYPES);
  const OPTIONS = keys.map((k) => OPTION_MAP[k]).filter(Boolean);

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-2xl [&>button.absolute]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="order-type-chooser-dialog"
      >
        <DialogHeader className="items-center">
          <div
            data-testid="order-type-locked-badge"
            className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-100 to-amber-50 dark:from-amber-950/40 dark:to-amber-900/20 border border-amber-300/60 dark:border-amber-700/50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-amber-800 dark:text-amber-300 shadow-sm animate-in fade-in slide-in-from-top-2 zoom-in-95 duration-500 mb-2"
          >
            <Lock size={12} strokeWidth={2.5} className="text-amber-600 dark:text-amber-400" />
            <span>Locked for this invoice</span>
          </div>
          <DialogTitle className="text-2xl font-bold text-center font-outfit">
            How is this order going out?
          </DialogTitle>
          <p className="text-sm text-muted-foreground text-center mt-1">
            Pick once — no re-prompt at checkout.
          </p>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 py-2">
          {OPTIONS.map(({ key, label, caption, Icon, tone, ring }) => (
            <button
              type="button"
              key={key}
              onClick={() => onChoose(key)}
              data-testid={`order-type-${key}-btn`}
              className={`group relative flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-transparent bg-gradient-to-br ${tone} text-white p-6 shadow-md ring-4 ring-transparent ${ring} transition-all active:scale-95`}
            >
              <div className="rounded-full bg-white/20 p-3 backdrop-blur">
                <Icon size={30} className="text-white" strokeWidth={2.2} />
              </div>
              <div className="text-lg font-bold tracking-tight">{label}</div>
              <div className="text-xs opacity-90">{caption}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
